/**
 * MeetingEditModal — M-Rhythm-3 / Sheet Migration Wave 3c
 *
 * Full-featured meeting editor with two modes:
 *   - 'edit'   — edit an existing meeting (fetches meeting by ID)
 *   - 'create' — new ad-hoc meeting creation flow
 *
 * Editable fields:
 *   Title, Date (DatePicker), Time (TimePicker), Duration (Input number),
 *   Accountable (AccountablePicker polymorphic), Moderator (AccountablePicker actor-only),
 *   Participants (ParticipantPicker), Agenda (AgendaEditor instance-mode),
 *   Status (Select — filtered by valid state-machine transitions),
 *   Tags (chip input — ad-hoc only)
 *
 * "Skip" flow: separate skip section below the form with reason Textarea.
 * "Edited" badge in header when is_recurring_exception === true.
 * Cancel with dirty-check: shows ConfirmDialog when there are unsaved changes
 *   (stays Modal per design contract R2 — sheet layers modal on top for confirms).
 *
 * Save: PATCH /api/v1/meetings/:id (edit) or POST /api/v1/meetings (create)
 * Shows showToast on success.
 *
 * Permission: Caller must have roros.meetings.write — checked at call site.
 *
 * Sheet Migration Wave 3c:
 *   Converted from Modal to FormSheet<MeetingSnapshot> using useFormSnapshot.
 *   - FormSheet is used as the right-slide Sheet visual wrapper.
 *   - baseline is passed as null (FormSheet dirty badge/inline banner disabled) so
 *     the existing ConfirmDialog-based dirty guard is preserved for test compatibility
 *     (AC-W3c-03 requires all existing unit tests to pass; tests validate ConfirmDialog).
 *   - Own dirty detection: deepEqual(current, baseline) from useFormSnapshot.
 *   - Optional urlKey prop enables URL routing when passed by CalendarPage (AC-W3c-02).
 *     MeetingChip (do-not-touch) omits urlKey → no URL routing from chip path.
 *   - Skip button rendered via FormSheet's headerExtras.
 *   - Wave 10 restore flow preserved (isAbsorbed → "Show anyway" via onSubmit).
 *
 * Compliance: M-Rhythm-3 plan §5 task 5. No raw <button>, no raw <input>.
 *             apiFetch only (no raw fetch). All strings via t().
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import { FormSheet } from '@/components/ui/FormSheet';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NumberInput } from '@/components/ui/NumberInput';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DatePicker } from '@/components/ui/DatePicker';
import { TimePicker } from '@/components/ui/TimePicker';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { AlertCircle, ArrowUpCircle } from 'lucide-react';
import { AccountablePicker } from '@/components/roros/AccountablePicker';
import { AgendaEditor } from '@/components/roros/AgendaEditor';
import type { AgendaItemDraft } from '@/components/roros/AgendaEditor';
import { ParticipantPicker } from '@/components/roros/ParticipantPicker';
import type { ParticipantEntry } from '@/components/roros/ParticipantPicker';
import { useRorosActor, useRorosActors } from '@/hooks/roros/actors';
import { useRorosDomains } from '@/hooks/roros/domains';
import { meetingKeys } from '@/hooks/roros/meetings';
import type {
  RorosMeetingDTO,
  MeetingStatus,
} from '@/types/roros/meetings';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosDomainDTO } from '@/types/roros/domains';
import { getAllowedTransitions, isTerminalStatus, statusLabel } from './meeting-status-utils';
// M-Rhythm-3-CB-001: useParticipantReconciliation deleted — replaced by
// PATCH /api/v1/meetings/:id/participants bulk endpoint (server-side atomic delta).

// ─── Types ─────────────────────────────────────────────────────────────────

/** F2.2: typed API error shape — avoids `as any` casts on error responses. */
interface ApiErrorResponse { error?: { message?: string; code?: string } }

const ACTOR_DIRECTORY_PARAMS = { pageSize: '100', sortBy: 'name', sortOrder: 'asc' } as const;

function actorDirectoryParams(q: string): Record<string, string> {
  const trimmed = q.trim();
  return trimmed ? { ...ACTOR_DIRECTORY_PARAMS, q: trimmed } : { ...ACTOR_DIRECTORY_PARAMS };
}

function mergeActors(...sources: Array<RorosActorDTO[] | RorosActorDTO | null | undefined>): RorosActorDTO[] {
  const byId = new Map<string, RorosActorDTO>();
  for (const source of sources) {
    if (!source) continue;
    const list = Array.isArray(source) ? source : [source];
    for (const actor of list) {
      byId.set(actor.id, actor);
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export interface MeetingEditModalProps {
  /** 'edit' opens an existing meeting; 'create' creates an ad-hoc meeting. */
  mode: 'edit' | 'create';
  /** Required in 'edit' mode. */
  meetingId?: string;
  /** Pre-fill scheduled_at for 'create' mode (ISO string). */
  initialScheduledAt?: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save. */
  onSaved?: () => void;
  /**
   * Optional URL routing key (Wave 3c). When set, FormSheet syncs
   * `?urlKey=meetingId` in the URL. Omit (default) to disable URL routing —
   * existing consumers (MeetingChip) need no changes.
   */
  urlKey?: string;
}

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

/**
 * All form fields as a flat snapshot type.
 * Same shape as the internal FormState — aliased for FormSheet<T> type parameter.
 */
interface FormState {
  title: string;
  scheduledDate: Date | null;
  scheduledTime: string | null; // "HH:mm"
  /** C12: use number | null so NumberInput can drive it directly. */
  durationMinutes: number | null;
  accountableActorId: string | null;
  accountableDomainId: string | null;
  moderatorActorId: string | null;
  status: MeetingStatus;
  tags: string[];
  participants: ParticipantEntry[];
  agenda: AgendaItemDraft[];
}

type MeetingSnapshot = FormState;

/**
 * Typed deep equality for FormState / MeetingSnapshot comparison.
 * CB-011: Replaces JSON.stringify-based dirty check to handle nested arrays/objects correctly.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a as object).sort();
    const keysB = Object.keys(b as object).sort();
    if (keysA.length !== keysB.length) return false;
    if (keysA.join(',') !== keysB.join(',')) return false;
    return keysA.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return a === b;
}

/**
 * Per-field diff for FormSheet<MeetingSnapshot>.
 * NOTE: baseline is passed as null to FormSheet (so FormSheet's own dirty
 * guard is disabled). This function is supplied as the FormSheet `diff` prop
 * but only used for the "Unsaved changes" badge (which only shows when isDirty=true).
 * Actual dirty-state guard uses ConfirmDialog driven by own deepEqual check.
 */
function diffMeetingSnapshots(
  base: MeetingSnapshot,
  cur: MeetingSnapshot,
): Partial<Record<keyof MeetingSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    scheduledDate: !deepEqual(base.scheduledDate, cur.scheduledDate),
    scheduledTime: base.scheduledTime !== cur.scheduledTime,
    durationMinutes: base.durationMinutes !== cur.durationMinutes,
    accountableActorId: base.accountableActorId !== cur.accountableActorId,
    accountableDomainId: base.accountableDomainId !== cur.accountableDomainId,
    moderatorActorId: base.moderatorActorId !== cur.moderatorActorId,
    status: base.status !== cur.status,
    tags: !deepEqual(base.tags, cur.tags),
    participants: !deepEqual(base.participants, cur.participants),
    agenda: !deepEqual(base.agenda, cur.agenda),
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function extractTime(isoString: string | null | undefined): string | null {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return null;
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

function extractDate(isoString: string | null | undefined): Date | null {
  if (!isoString) return null;
  const d = new Date(isoString);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * W8-6: Timezone-safe date+time combinator.
 *
 * Bug in previous version: `date.toISOString().slice(0, 10)` returns the UTC date,
 * which can be one day behind the user's local date for users in UTC+ zones
 * (e.g. midnight in CEST = previous day in UTC). Combined with treating `time`
 * as UTC, this produced a 22-hour drift for users in UTC+2.
 *
 * Fix: extract LOCAL date components from `date` (getFullYear/getMonth/getDate),
 * then construct a Luxon DateTime in the browser's local timezone, and convert
 * to UTC for storage. This correctly handles DST transitions.
 */
function combineDateTime(date: Date | null, time: string | null): string | null {
  if (!date) return null;
  const [h, m] = (time ?? '00:00').split(':').map(Number);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dt = DateTime.fromObject(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1, // getMonth() is 0-indexed
      day: date.getDate(),
      hour: h ?? 0,
      minute: m ?? 0,
      second: 0,
      millisecond: 0,
    },
    { zone: tz },
  );
  return dt.toUTC().toISO();
}

function nearestHourBoundary(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return `${d.getHours().toString().padStart(2, '0')}:00`;
}

function toDraftItems(agendaItems: RorosMeetingDTO['agendaItems']): AgendaItemDraft[] {
  let counter = 0;
  return (agendaItems ?? []).map((item) => ({
    key: `existing-${++counter}`,
    title: item.title,
    accountableActorId: item.accountableActorId,
    durationMinutes: item.durationMinutes,
  }));
}

function toParticipantEntries(
  participants: RorosMeetingDTO['participants'],
): ParticipantEntry[] {
  let counter = 0;
  return (participants ?? []).map((p) => ({
    key: `existing-${++counter}`,
    participantKind: p.participantKind,
    actorId: p.actorId,
    domainId: p.domainId,
    role: p.role,
    displayName: p.displayName,
  }));
}

/**
 * Build initial FormState from a loaded meeting DTO.
 * Pure function — no component state dependency.
 */
function initFormFromMeeting(m: RorosMeetingDTO): FormState {
  return {
    title: m.title,
    scheduledDate: extractDate(m.scheduledAt),
    scheduledTime: extractTime(m.scheduledAt),
    durationMinutes: m.durationMinutes ?? null,
    accountableActorId: m.accountableActorId,
    accountableDomainId: m.accountableDomainId,
    moderatorActorId: m.moderatorActorId,
    status: m.status,
    tags: m.tags ?? [],
    participants: toParticipantEntries(m.participants),
    agenda: toDraftItems(m.agendaItems),
  };
}

// ─── Main component ─────────────────────────────────────────────────────────

export function MeetingEditModal({
  mode,
  meetingId,
  initialScheduledAt,
  isOpen,
  onClose,
  onSaved,
  urlKey,
}: MeetingEditModalProps) {
  const queryClient = useQueryClient();
  const [meeting, setMeeting] = useState<RorosMeetingDTO | null>(null);
  const [isLoadingMeeting, setIsLoadingMeeting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSkipOpen, setIsSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [isSkipping, setIsSkipping] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [participantActorSearch, setParticipantActorSearch] = useState('');
  const [accountableActorSearch, setAccountableActorSearch] = useState('');

  const debouncedParticipantActorSearch = useDebounce(participantActorSearch.trim(), 250);
  const debouncedAccountableActorSearch = useDebounce(accountableActorSearch.trim(), 250);

  // F-MUT1-P9: keep the base first-100 actor directory for unchanged pickers,
  // but run participant/accountable search through /api/v1/actors?q=... so
  // orgs with >100 actors can find actors outside the initial page.
  const baseActorsQuery = useRorosActors(actorDirectoryParams(''));
  const participantActorsQuery = useRorosActors(actorDirectoryParams(debouncedParticipantActorSearch));
  const accountableActorsQuery = useRorosActors(actorDirectoryParams(debouncedAccountableActorSearch));
  const { data: domainsList = [] } = useRorosDomains();

  // ── Form state via useFormSnapshot (Wave 3c) ─────────────────────────────
  //
  // loadedMeetingRef holds the last fetched meeting data. The initialFactory
  // references it so that reset() (called after fetch) seeds both state and
  // baseline with the freshly loaded data.
  //
  // depKey: meetingId (edit) or null (create). On the false→true isOpen
  // transition, the hook re-seeds from initialFactory with whatever data is
  // in loadedMeetingRef. Then the apiFetch effect calls reset() again once
  // data is ready, updating both state and baseline to match the API response.
  const loadedMeetingRef = useRef<RorosMeetingDTO | null>(null);

  const defaultCreateForm = useCallback((): FormState => {
    const initDate = initialScheduledAt
      ? new Date(initialScheduledAt)
      : (() => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; })();
    const initTime = initialScheduledAt
      ? extractTime(initialScheduledAt)
      : nearestHourBoundary();
    return {
      title: '',
      scheduledDate: initDate,
      scheduledTime: initTime,
      durationMinutes: null,
      accountableActorId: null,
      accountableDomainId: null,
      moderatorActorId: null,
      status: 'scheduled',
      tags: [],
      participants: [],
      agenda: [],
    };
  }, [initialScheduledAt]);

  const { state, setField, baseline, current, reset } = useFormSnapshot<MeetingSnapshot>({
    initialFactory: () => {
      if (
        mode === 'edit' &&
        loadedMeetingRef.current &&
        loadedMeetingRef.current.id === meetingId
      ) {
        return initFormFromMeeting(loadedMeetingRef.current);
      }
      return defaultCreateForm();
    },
    isOpen,
    depKey: mode === 'edit' ? meetingId ?? null : null,
  });

  // Destructure for JSX and submit handler.
  const {
    title: formTitle,
    scheduledDate,
    scheduledTime,
    durationMinutes,
    accountableActorId,
    accountableDomainId,
    moderatorActorId,
    status: formStatus,
    tags,
    participants,
    agenda,
  } = state;

  const selectedAccountableActorQuery = useRorosActor(accountableActorId ?? '');

  const actors = useMemo(() => mergeActors(
    (baseActorsQuery.data ?? []) as RorosActorDTO[],
    (participantActorsQuery.data ?? []) as RorosActorDTO[],
    (accountableActorsQuery.data ?? []) as RorosActorDTO[],
    (selectedAccountableActorQuery.data ?? null) as RorosActorDTO | null,
  ), [
    baseActorsQuery.data,
    participantActorsQuery.data,
    accountableActorsQuery.data,
    selectedAccountableActorQuery.data,
  ]);
  // M-Rhythm-3-CB-012: typed domains — ParticipantPicker and AccountablePicker
  // both accept RorosDomainDTO[] for their domains prop.
  const domains = useMemo(() => domainsList as RorosDomainDTO[], [domainsList]);

  // ── Own dirty detection ───────────────────────────────────────────────────
  //
  // FormSheet receives baseline=null so its built-in dirty guard is disabled.
  // We compute isDirty ourselves using the baseline from useFormSnapshot,
  // and surface it via ConfirmDialog (AC requirement: stays Modal per R2).
  const isDirty = useMemo(() => !deepEqual(current, baseline), [current, baseline]);

  // ── Load existing meeting in edit mode ───────────────────────────────────

  useEffect(() => {
    if (mode !== 'edit' || !meetingId || !isOpen) return;

    setIsLoadingMeeting(true);
    setLoadError(null);

    apiFetch(`/api/v1/meetings/${meetingId}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error((data as any)?.error?.message ?? t('Failed to load meeting'));
        }
        return res.json() as Promise<{ data: RorosMeetingDTO }>;
      })
      .then(({ data }) => {
        setMeeting(data);
        loadedMeetingRef.current = data;
        // Re-seed both state and baseline from the freshly loaded meeting.
        reset();
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : t('Failed to load meeting');
        setLoadError(message);
      })
      .finally(() => {
        setIsLoadingMeeting(false);
      });
  }, [mode, meetingId, isOpen, reset]);

  // ── Reset skip + load error on open transition ───────────────────────────
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (isOpen && !wasOpen) {
      setLoadError(null);
      setIsSkipOpen(false);
      setSkipReason('');
    }
  }, [isOpen]);

  // ── Derived state ────────────────────────────────────────────────────────

  // isAdHoc is no longer needed for UI gating (tags removed, moderator removed)
  // but remains for possible future use or by external callers.
  const isTerminal = meeting ? isTerminalStatus(meeting.status) : false;
  const isReadOnly = isTerminal || isSaving;

  // Wave 10: absorbed = auto-cancelled by a higher-cadence meeting on the same day
  const isAbsorbed = mode === 'edit' && Boolean(meeting?.absorbedByMeetingId);

  // Valid status options: current status + allowed transitions
  const statusOptions = useMemo(() => {
    const currentStatus = meeting?.status ?? 'scheduled';
    const allowed = getAllowedTransitions(currentStatus as MeetingStatus);
    const options: MeetingStatus[] = [currentStatus as MeetingStatus, ...allowed];
    return options.map((s) => ({
      value: s,
      label: statusLabel(s),
    }));
  }, [meeting]);

  // Participant actors (for instance-mode AgendaEditor)
  const participantActors = useMemo(() => {
    return participants
      .filter((p) => p.participantKind === 'actor' && p.actorId)
      .map((p) => actors.find((a) => a.id === p.actorId))
      .filter((a): a is RorosActorDTO => a != null);
  }, [participants, actors]);

  // Computed agenda duration helper
  const computedDuration = useMemo(() => {
    const total = agenda.reduce((sum, item) => sum + (item.durationMinutes ?? 0), 0);
    return total > 0 ? total : null;
  }, [agenda]);

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!formTitle.trim()) {
      showToast.error(t('Title is required'));
      return;
    }
    if (!scheduledDate) {
      showToast.error(t('Date is required'));
      return;
    }

    const scheduledAt = combineDateTime(scheduledDate, scheduledTime);
    if (!scheduledAt) {
      showToast.error(t('Invalid date/time'));
      return;
    }

    setIsSaving(true);

    try {
      if (mode === 'create') {
        const body: Record<string, unknown> = {
          title: formTitle.trim(),
          scheduled_at: scheduledAt,
          duration_minutes: durationMinutes,
          accountable_actor_id: accountableActorId,
          accountable_domain_id: accountableDomainId,
          moderator_actor_id: moderatorActorId,
          tags,
          participants: participants.map((p) => ({
            participant_kind: p.participantKind,
            actor_id: p.actorId,
            domain_id: p.domainId,
            role: p.role,
          })),
          agenda: agenda.map((item, idx) => ({
            title: item.title,
            accountable_actor_id: item.accountableActorId,
            duration_minutes: item.durationMinutes,
            sort_order: (idx + 1) * 10,
          })),
        };

        const res = await apiFetch('/api/v1/meetings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          // F2.1+F2.2: log parse errors; use typed interface instead of `as any`
          const data = await res.json().catch((e: unknown) => {
            console.warn('[MeetingEditModal] Failed to parse create error response', e);
            return {} as ApiErrorResponse;
          });
          throw new Error((data as ApiErrorResponse).error?.message ?? t('Failed to create meeting'));
        }

        showToast.success(t('Meeting created'));
      } else {
        // meetingId is required in edit mode — guard here to narrow the type.
        if (!meetingId) {
          showToast.error(t('Meeting ID is required'));
          return;
        }

        // Build delta payload (only fields that changed)
        const delta: Record<string, unknown> = {};

        if (formTitle !== baseline.title) delta.title = formTitle.trim();
        const origScheduledAt = combineDateTime(baseline.scheduledDate, baseline.scheduledTime);
        if (scheduledAt !== origScheduledAt) delta.scheduled_at = scheduledAt;
        if (durationMinutes !== baseline.durationMinutes) delta.duration_minutes = durationMinutes;
        if (accountableActorId !== baseline.accountableActorId) delta.accountable_actor_id = accountableActorId;
        if (accountableDomainId !== baseline.accountableDomainId) delta.accountable_domain_id = accountableDomainId;
        if (moderatorActorId !== baseline.moderatorActorId) delta.moderator_actor_id = moderatorActorId;
        if (formStatus !== baseline.status) delta.status = formStatus;
        if (!deepEqual(tags, baseline.tags)) delta.tags = tags;

        if (Object.keys(delta).length > 0) {
          const res = await apiFetch(`/api/v1/meetings/${meetingId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(delta),
          });

          if (!res.ok) {
            // F2.1+F2.2: log parse errors; use typed interface instead of `as any`
            const data = await res.json().catch((e: unknown) => {
              console.warn('[MeetingEditModal] Failed to parse update error response', e);
              return {} as ApiErrorResponse;
            });
            throw new Error((data as ApiErrorResponse).error?.message ?? t('Failed to update meeting'));
          }
        }

        // Update agenda separately if changed
        if (!deepEqual(agenda, baseline.agenda)) {
          const agendaRes = await apiFetch(`/api/v1/meetings/${meetingId}/agenda`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: agenda.map((item) => ({
                title: item.title,
                accountable_actor_id: item.accountableActorId,
                duration_minutes: item.durationMinutes,
              })),
            }),
          });

          if (!agendaRes.ok) {
            // F2.1+F2.2: log parse errors; use typed interface instead of `as any`
            const data = await agendaRes.json().catch((e: unknown) => {
              console.warn('[MeetingEditModal] Failed to parse agenda error response', e);
              return {} as ApiErrorResponse;
            });
            throw new Error((data as ApiErrorResponse).error?.message ?? t('Failed to update agenda'));
          }
        }

        // AC-MUT1-7: Only call the bulk-replace endpoint when participants have
        // actually changed. This prevents spurious 422 "terminal status" errors
        // when the user opens a completed/skipped meeting (which can happen during
        // a brief loading window) without changing participants, and ensures that
        // legitimate participant changes on scheduled meetings always persist.
        // M-Rhythm-3-CB-001: server computes add/remove/update delta atomically.
        if (!deepEqual(participants, baseline.participants)) {
          const participantsRes = await apiFetch(`/api/v1/meetings/${meetingId}/participants`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              participants: participants.map((p) => ({
                participant_kind: p.participantKind,
                actor_id: p.actorId ?? null,
                domain_id: p.domainId ?? null,
                role: p.role,
              })),
            }),
          });

          if (!participantsRes.ok) {
            const data = await participantsRes.json().catch((e: unknown) => {
              console.warn('[MeetingEditModal] Failed to parse participants error response', e);
              return {} as ApiErrorResponse;
            });
            throw new Error((data as ApiErrorResponse).error?.message ?? t('Failed to update participants'));
          }
        }

        showToast.success(t('Meeting updated'));
      }

      // Invalidate calendar queries — non-fatal: sheet must close even if cache
      // invalidation fails (e.g. transient network error after successful save).
      await queryClient.invalidateQueries({ queryKey: meetingKeys.all }).catch((e: unknown) => {
        console.warn('[MeetingEditModal] Cache invalidation failed (non-fatal):', e);
      });
      onSaved?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('An error occurred');
      showToast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Skip ────────────────────────────────────────────────────────────────

  const handleSkip = async () => {
    if (!meetingId) return;
    setIsSkipping(true);
    try {
      const res = await apiFetch(`/api/v1/meetings/${meetingId}/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: skipReason || undefined }),
      });

      if (!res.ok) {
        // F2.1+F2.2: log parse errors; use typed interface instead of `as any`
        const data = await res.json().catch((e: unknown) => {
          console.warn('[MeetingEditModal] Failed to parse skip error response', e);
          return {} as ApiErrorResponse;
        });
        throw new Error((data as ApiErrorResponse).error?.message ?? t('Failed to skip meeting'));
      }

      showToast.success(t('Meeting skipped'));
      await queryClient.invalidateQueries({ queryKey: meetingKeys.all }).catch((e: unknown) => {
        console.warn('[MeetingEditModal] Cache invalidation failed (non-fatal):', e);
      });
      onSaved?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('An error occurred');
      showToast.error(message);
    } finally {
      setIsSkipping(false);
    }
  };

  // ── Restore (Wave 10) ────────────────────────────────────────────────────

  const handleRestore = async () => {
    if (!meetingId) return;
    setIsRestoring(true);
    try {
      const res = await apiFetch(`/api/v1/meetings/${meetingId}/restore`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const data = await res.json().catch((e: unknown) => {
          console.warn('[MeetingEditModal] Failed to parse restore error response', e);
          return {} as ApiErrorResponse;
        });
        throw new Error((data as ApiErrorResponse).error?.message ?? t('Failed to restore meeting'));
      }

      showToast.success(t('Meeting restored'));
      await queryClient.invalidateQueries({ queryKey: meetingKeys.all }).catch((e: unknown) => {
        console.warn('[MeetingEditModal] Cache invalidation failed (non-fatal):', e);
      });
      onSaved?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('An error occurred');
      showToast.error(message);
    } finally {
      setIsRestoring(false);
    }
  };

  // ── Close / discard ──────────────────────────────────────────────────────
  //
  // Design contract R2: the dirty-state guard uses ConfirmDialog (stays Modal
  // even when layered above a Sheet). FormSheet receives onClose = handleClose,
  // and its own dirty guard is disabled via baseline=null. When the user
  // dismisses (Cancel, X, backdrop, Escape), FormSheet calls onClose()=handleClose().
  // If dirty, we intercept with ConfirmDialog; otherwise forward to the prop onClose.

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // ── Sheet title ──────────────────────────────────────────────────────────

  const sheetTitle: React.ReactNode = mode === 'create'
    ? t('New ad-hoc meeting')
    : meeting
    ? (
      <span className="flex items-center gap-2">
        {meeting.title}
        {meeting.isRecurringException && (
          <Badge variant="warning" className="text-xs">
            {t('Edited')}
          </Badge>
        )}
      </span>
    )
    : t('Meeting');

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <FormSheet<MeetingSnapshot>
        isOpen={isOpen}
        onClose={handleClose}
        title={sheetTitle}
        mode={mode === 'create' ? 'create' : 'edit'}
        /**
         * Wave 3c: pass baseline=null to disable FormSheet's built-in dirty
         * guard (inline discard banner). Dirty state is managed via own
         * isDirty + ConfirmDialog to satisfy AC-W3c-03 (existing unit tests
         * validate ConfirmDialog behavior and cannot be modified).
         */
        baseline={null}
        current={current}
        diff={diffMeetingSnapshots}
        onSubmit={isAbsorbed ? handleRestore : handleSave}
        isSubmitting={isSaving || isRestoring}
        submitLabel={isAbsorbed ? t('Show anyway') : t('Save')}
        submittingLabel={isAbsorbed ? t('Restoring…') : t('Saving…')}
        submitDisabled={isTerminal && !isAbsorbed}
        headerExtras={
          mode === 'edit' && meeting && !isTerminal && !isAbsorbed ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSkipOpen(!isSkipOpen)}
              disabled={isSaving || isSkipping}
              aria-expanded={isSkipOpen}
              aria-controls="skip-section"
            >
              {t('Skip')}
            </Button>
          ) : undefined
        }
        width="md"
        urlKey={urlKey}
        urlId={meetingId}
        submitTestId="meeting-save-btn"
      >
        {/* Loading state for edit mode */}
        {isLoadingMeeting && <LoadingState />}

        {/* Error state for edit mode */}
        {loadError && !isLoadingMeeting && (
          <EmptyState
            icon={<AlertCircle className="h-10 w-10 text-destructive" />}
            title={t('Failed to load meeting')}
            description={loadError}
          />
        )}

        {/* Terminal state notice */}
        {isTerminal && !isAbsorbed && (
          <div className="mb-4 rounded-lg bg-muted/60 border border-border px-4 py-3 text-sm text-muted-foreground">
            {t('This meeting is in a terminal status and cannot be edited.')}
          </div>
        )}

        {/* Wave 10: Absorbed-by banner */}
        {isAbsorbed && (
          <div
            className="mb-4 rounded-lg border border-warning-300 bg-warning-50 px-4 py-3 text-sm flex items-start gap-2"
            data-testid="meeting-absorbed-banner"
          >
            <ArrowUpCircle className="h-4 w-4 mt-0.5 shrink-0 text-warning-600" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-medium text-warning-800">
                {t('This meeting was auto-cancelled because a higher-cadence meeting falls on the same day.')}
              </p>
              <p className="mt-0.5 text-warning-700 text-xs">
                {t('Click "Show anyway" to restore it. It will stay visible even after the schedule is regenerated.')}
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        {(!isLoadingMeeting || mode === 'create') && !loadError && (
          <div className="space-y-4">
            {/* Title */}
            <Input
              label={t('Title')}
              value={formTitle}
              onChange={(e) => setField('title', e.target.value)}
              disabled={isReadOnly}
              placeholder={t('Meeting title')}
              required
              data-testid="meeting-edit-title"
            />

            {/* Date + Time row */}
            <div className="grid grid-cols-2 gap-3">
              <DatePicker
                label={t('Date')}
                value={scheduledDate}
                onChange={(d) => setField('scheduledDate', d)}
                disabled={isReadOnly}
              />
              <TimePicker
                label={t('Time')}
                value={scheduledTime}
                onChange={(t) => setField('scheduledTime', t)}
                disabled={isReadOnly}
              />
            </div>
              {/* CB-010: Note for past-date pre-fills — backdating is valid (historical record-keeping) */}
              {scheduledDate && scheduledDate < new Date() && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <span aria-hidden="true">📅</span>
                  {t('Note: this date is in the past')}
                </p>
              )}

            {/* Duration — C12: NumberInput with 5-min spinner */}
            <div>
              <NumberInput
                label={t('Duration (minutes)')}
                value={durationMinutes}
                onChange={(v) => setField('durationMinutes', v)}
                disabled={isReadOnly}
                min={0}
                max={600}
                step={5}
                placeholder={computedDuration ? `${computedDuration} (${t('from agenda')})` : t('Optional')}
                hint={computedDuration && durationMinutes === null
                  ? `${t('Auto: sum of agenda items')} = ${computedDuration} ${t('min')}`
                  : undefined
                }
                data-testid="meeting-edit-duration"
              />
            </div>

            {/* Accountable — AC-MUT1-1: moderator removed from UI; server auto-assigns
                the system Meeting Moderator. Overview shows it read-only. */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Accountable')}
              </label>
              <AccountablePicker
                value={{
                  actorId: accountableActorId,
                  domainId: accountableDomainId,
                }}
                onChange={({ actorId, domainId }) => {
                  setField('accountableActorId', actorId);
                  setField('accountableDomainId', domainId);
                }}
                actors={actors}
                domains={domains}
                mode="polymorphic"
                disabled={isReadOnly}
                actorSearchValue={accountableActorSearch}
                onActorSearchChange={setAccountableActorSearch}
                actorSearchLoading={accountableActorsQuery.isFetching && !!accountableActorSearch.trim()}
                placeholder={t('None')}
                data-testid="meeting-edit-accountable"
              />
            </div>

            {/* Participants */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Participants')}
              </label>
              <ParticipantPicker
                participants={participants}
                onChange={(ps) => setField('participants', ps)}
                actors={actors}
                domains={domains}
                disabled={isReadOnly}
                searchValue={participantActorSearch}
                onSearchChange={setParticipantActorSearch}
                isSearching={participantActorsQuery.isFetching && !!participantActorSearch.trim()}
                data-testid="meeting-edit-participants"
              />
            </div>

            {/* Agenda */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Agenda')}
              </label>
              <AgendaEditor
                items={agenda}
                onChange={(items) => setField('agenda', items)}
                mode="instance"
                participantActors={participantActors}
                disabled={isReadOnly}
                data-testid="meeting-edit-agenda"
              />
            </div>

            {/* Status (edit mode only; filtered to valid transitions) */}
            {mode === 'edit' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('Status')}
                </label>
                <Select
                  value={formStatus}
                  onValueChange={(v) => setField('status', v as MeetingStatus)}
                  options={statusOptions}
                  disabled={isReadOnly || statusOptions.length <= 1}
                  data-testid="meeting-edit-status"
                />
              </div>
            )}

            {/* AC-MUT1-4: Tags input removed from meeting dialogs (user-confirmed 2026-06-10).
                tags DB column + API field preserved; existing meetings with stored tags
                continue to load/save without error. UI removal only. */}

            {/* Skip section (edit mode only) */}
            {mode === 'edit' && meeting && !isTerminal && isSkipOpen && (
              <div
                id="skip-section"
                className="rounded-lg border border-warning-300 bg-warning-50 p-4 space-y-3"
                data-testid="meeting-skip-section"
              >
                <p className="text-sm font-medium text-warning-800">{t('Skip this occurrence')}</p>
                <Textarea
                  label={t('Reason (optional)')}
                  placeholder={t('Why is this meeting being skipped?')}
                  value={skipReason}
                  onChange={(e) => setSkipReason(e.target.value)}
                  disabled={isSkipping}
                  rows={2}
                  data-testid="meeting-skip-reason"
                />
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleSkip}
                    disabled={isSkipping}
                    data-testid="meeting-skip-confirm"
                  >
                    {isSkipping ? t('Skipping…') : t('Confirm skip')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsSkipOpen(false)}
                    disabled={isSkipping}
                  >
                    {t('Cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </FormSheet>

      {/* Dirty-check discard confirmation — stays Modal per design contract R2
          (sheet layers modal on top for confirms). */}
      <ConfirmDialog
        isOpen={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
        title={t('Discard changes?')}
        description={t('You have unsaved changes. Are you sure you want to discard them?')}
        confirmText={t('Discard')}
        cancelText={t('Keep editing')}
        variant="danger"
      />
    </>
  );
}
